import * as Select from '@radix-ui/react-select';
import type { JSX, ReactNode } from 'react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DropdownTriggerClearButton } from '~/chords/DropdownSingleSelect/components/DropdownTriggerClearButton';
import { chevron } from '~/chords/DropdownSingleSelect/styles';
import type {
  DropdownState,
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/chords/DropdownSingleSelect/types';
import { Icon } from '~/chords/Icon';
import { cn } from '~/chords/lib/cn';

interface DropdownTriggerProps extends RadixUiProps {
  id?: string;
  placeholder?: string;
  selectedOption?: InternalDropdownOption;
  isOpen: boolean;
  state?: DropdownState;
  disabled?: boolean;
  OptionTemplate?: OptionTemplateComponent;
  isClearable?: boolean;
  onClear?: () => void;
}

export const DropdownTrigger = forwardRef<
  HTMLButtonElement,
  DropdownTriggerProps
>(
  (
    {
      id,
      placeholder,
      selectedOption,
      isOpen,
      state,
      disabled,
      OptionTemplate,
      isClearable,
      onClear,
      radix = {},
    },
    ref,
  ) => {
    const { t } = useTranslation('common');

    const placeholderText = placeholder || t('dropdownTree.placeholder');
    let valueDisplay: JSX.Element | ReactNode = placeholderText;

    if (selectedOption) {
      if (OptionTemplate) {
        valueDisplay = (
          <OptionTemplate option={selectedOption} isTrigger={true} />
        );
      } else {
        valueDisplay = (
          <span className="truncate text-default">
            {selectedOption.label || selectedOption.value}
          </span>
        );
      }
    }

    const showClearButton = Boolean(isClearable && selectedOption && !disabled);

    return (
      <div className="group relative w-full">
        <Select.Trigger
          {...radix.selectTrigger}
          ref={ref}
          id={id}
          className={cn(
            'relative flex w-full cursor-pointer items-stretch justify-between gap-sm rounded-xs py-xs pr-sm text-body-sm leading-8',
            'outline outline-1 transition-colors-with-outline duration-300 ease-out bg-emphasis-default outline-emphasis-subtle',
            'enabled:hover:bg-emphasis-subtle enabled:hover:outline-emphasis-strong disabled:cursor-not-allowed disabled:outline-transparent',
            {
              'pl-md': !(OptionTemplate && selectedOption),
              'outline-error-default': state === 'error',
              'outline-success-default': state === 'success',
              'outline-warning-default': state === 'warning',
            },
            radix.selectTrigger?.className,
          )}
          aria-invalid={state === 'error'}
        >
          <Select.Value
            {...radix.selectValue}
            placeholder={
              <div className="flex min-w-0 items-center text-subtle">
                <div className="truncate">{placeholderText}</div>
              </div>
            }
            asChild
          >
            <div
              className={cn(
                { 'text-disabled': disabled },
                'flex items-center truncate',
                radix.selectValue?.className,
              )}
            >
              {valueDisplay}
            </div>
          </Select.Value>

          <div className="flex shrink-0 items-center gap-md">
            {showClearButton ? (
              // Reserves space for the clear button, otherwise, really long
              // labels that get truncated will appear under the clear button.
              <span aria-hidden className="w-md" />
            ) : null}
            <Select.Icon
              {...radix.selectIcon}
              className={cn(
                'flex items-center text-default',
                radix.selectIcon?.className,
                { 'text-disabled': disabled },
              )}
            >
              <div
                className={cn(
                  chevron({ state: isOpen ? 'open' : 'default' }),
                  'mr-sm',
                )}
              >
                <Icon size="md" type="chevron-down" />
              </div>
            </Select.Icon>
          </div>
        </Select.Trigger>

        {showClearButton ? (
          <DropdownTriggerClearButton
            onClear={onClear}
            className="absolute right-[3rem] top-1/2 -translate-y-1/2"
          />
        ) : null}
      </div>
    );
  },
);

DropdownTrigger.displayName = 'DropdownTrigger';
